// Relative path: src/components/Recommendations/TopRow.js
import React, { useState, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import GalleryPopup from '../Popup/GalleryPopup';
import DetailsPopup from '../Popup/DetailsPopup';

// CRA/Webpack inline-SVG imports (for Vite, use `?react` variant)
import { ReactComponent as EstimatedCostIcon } from '../../images/estimated_cost.svg';
import { ReactComponent as EstimatedAvailabilityIcon } from '../../images/estimated_availability.svg';

// Scoped CSS that forces all shapes inside the SVG to use currentColor
const ForcedSvgStyles = () => (
  <style>{`
    .svg-force-gray svg,
    .svg-force-gray svg * {
      fill: currentColor !important;
      stroke: currentColor !important;
    }
  `}</style>
);

const TopRow = forwardRef(({
  imageUrl = '',
  optionTitle = 'Option Title',
  overallRating = 'Highly Rated',
  overallRatingColor = 'green',
  optionDescription = 'Option description goes here',
  optionDescriptionLong = '',
  estimatedCost = '3000-6000',
  estimatedAvailability = 'Within 2 Months',
  optionRank = 1,
  galleryImages = [],
  optionIndex = 0
}, ref) => {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  // Get the background color based on option index
  const getBackgroundColor = () => {
    const colors = ['bg-option1_main_soft', 'bg-option2_main_soft', 'bg-option3_main_soft'];
    return colors[optionIndex % colors.length] || 'bg-option1_main';
  };

  // Find the index of the main image in the gallery
  const getInitialImageIndex = () => {
    if (!imageUrl || !galleryImages || galleryImages.length === 0) return 0;
    const index = galleryImages.findIndex(img => img.downloadURL === imageUrl);
    return index >= 0 ? index : 0;
  };

  // Create a portal for the gallery popup
  const renderGalleryPopup = () => {
    if (!isGalleryOpen || galleryImages.length === 0) return null;
    if (typeof document !== 'undefined') {
      return ReactDOM.createPortal(
        <GalleryPopup
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          images={galleryImages}
          initialIndex={getInitialImageIndex()}
        />,
        document.body
      );
    }
    return null;
  };

  // Create a portal for the description popup
  const renderDescriptionPopup = () => {
    if (!isDescriptionOpen) return null;

    // Prepare paragraphs array, filtering out empty strings
    const paragraphs = [
      optionDescription,        // Short description first
      optionDescriptionLong     // Long description second
    ].filter(Boolean);

    if (typeof document !== 'undefined') {
      return ReactDOM.createPortal(
        <DetailsPopup
          isOpen={isDescriptionOpen}
          onClose={() => setIsDescriptionOpen(false)}
          title={`${optionTitle} Description`}
          paragraphs={paragraphs}
        />,
        document.body
      );
    }
    return null;
  };

  // Background opacity class
  const bgOpacityClass = 'bg-opacity-100 md:bg-opacity-0';

  return (
    <>
      {/* Keep this high so the CSS applies before icons render */}
      <ForcedSvgStyles />

      <div
        ref={ref}
        className={`flex items-start px-5 pb-2 pt-6 ${getBackgroundColor()} ${bgOpacityClass} md:h-full`}
      >
        {/* Image/Avatar - clickable */}
        <div
          className="w-36 h-36 bg-gray-200 rounded-lg mr-4 flex-shrink-0 cursor-pointer relative overflow-hidden"
          onClick={() => {
            if (galleryImages && galleryImages.length > 0) {
              setIsGalleryOpen(true);
            }
          }}
        >
          {imageUrl && (
            <img
              src={imageUrl}
              alt={optionTitle}
              className="w-full h-full object-cover rounded-lg"
            />
          )}

          {/* Gallery indicator - only show if multiple images */}
          {galleryImages && galleryImages.length > 1 && (
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs rounded-full px-2 py-1 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{galleryImages.length}</span>
            </div>
          )}
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="font-bold text-gray-800 text-lg truncate" title={optionTitle}>
            {optionTitle}
            {/* <Label text={overallRating} color={overallRatingColor} /> */}
          </h3>

          {/* Description - clickable */}
          <p
            className="text-gray-600 text-sm md:text-xs mt-0 cursor-pointer hover:text-gray-800"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxHeight: '3em', // approx 2 lines
            }}
            onClick={() => setIsDescriptionOpen(true)}
          >
            {optionDescription}
          </p>

          {/* Cost and Availability Info */}
          <div className="mt-0">
            <table className="border-collapse">
              <tbody>
                {/* Estimated Cost Row */}
                <tr>
                  <td className="w-6 h-6 relative py-2" rowSpan={2}>
                    <span className="svg-force-gray text-gray-900 inline-flex">
                      <EstimatedCostIcon className="w-6 h-6" aria-label="Estimated Cost" />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="pt-0 pb-0 px-2 text-sm text-left text-gray-700 font-bold">
                    ${estimatedCost}
                  </td>
                </tr>

                {/* Estimated Availability Row */}
                <tr>
                  <td className="w-6 h-6 relative pb-2" rowSpan={2}>
                    <span className="svg-force-gray text-gray-900 inline-flex">
                      <EstimatedAvailabilityIcon className="w-6 h-6" aria-label="Estimated Availability" />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="pt-0 pb-0 px-2 text-sm text-left text-gray-700 font-bold">
                    {estimatedAvailability}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Gallery Popup */}
        {renderGalleryPopup()}

        {/* Description Popup */}
        {renderDescriptionPopup()}
      </div>
    </>
  );
});

// Add display name for easier debugging
TopRow.displayName = 'TopRow';

export default TopRow;
